import {
	createRouter,
	createWebHistory,
	type RouteRecordRaw,
} from 'vue-router';

import { ROUTES } from '@/constants/routes';
import i18n from '@/i18n';
import authService from '@/modules/auth/services/authService';
import { useBreadcrumbsStore } from '@/store/breadcrumbsStore';

import type { BreadcrumbProps } from '@/dsComponents/breadcrumbs/types';

import { useModalStore } from '@/dsComponents/modals/modalStore';

import {
	externalRoutes as activeProposalExternalRoutes,
	routesInApp as activeProposalRoutesInApp,
} from '@/modules/activeProposal/routes';
import agencyRoutes from '@/modules/agency/routes';
// Import modules routes
import authRoutes from '@/modules/auth/routes';
import clientsRoutes from '@/modules/clients/routes';
import companyNeedRoutes from '@/modules/companyNeed/routes';
import dashboardRoutes from '@/modules/dashboard/routes';
import designReviewRoutes from '@/modules/designPreview/routes';
import paymentRequestRoutes from '@/modules/paymentRequest/routes';
import userRoutes from '@/modules/user/routes';
import workerRoutes from '@/modules/worker/routes';

const routes: Array<RouteRecordRaw> = [
	{
		path: '/',
		name: '__root',
		redirect: 'app',
	},
	...activeProposalExternalRoutes,
	...authRoutes,
	{
		path: '/talentplug-return',
		name: ROUTES.TALENTPLUG_RETURN,
		beforeEnter: (to, _from, next) => {
			const tokens = authService.getTokens();
			if (!tokens?.access_token) {
				next({ name: ROUTES.LOGIN, query: { redirect: to.fullPath } });
			} else {
				next();
			}
		},
		component: () =>
			import(
				/* webpackChunkName: "talentplug-return" */ '../views/TalentplugReturn.vue'
			),
	},
	{
		path: '/app',
		name: ROUTES.APP._ROOT,
		redirect: { name: ROUTES.APP.HOME },
		// biome-ignore lint/correctness/noUnusedFunctionParameters: Init Biome
		beforeEnter: (to, from, next) => {
			const tokens = authService.getTokens();
			if (!tokens?.access_token) {
				next({ name: ROUTES.LOGIN, query: { redirect: to.fullPath } });
			} else {
				next();
			}
		},
		component: () =>
			import(/* webpackChunkName: "layout" */ '../views/Layout.vue'),
		children: [
			...dashboardRoutes,
			{
				path: 'notifications',
				name: ROUTES.APP.NOTIFICATIONS,
				component: () =>
					import(
						/* webpackChunkName: "notifications" */ '../views/Notifications.vue'
					),
				meta: {
					breadcrumbs: <BreadcrumbProps>{
						items: [
							{
								label: i18n.global.t('breadcrumbs.notifications.root'),
								icon: 'inbox',
								link: { name: ROUTES.APP.NOTIFICATIONS },
							},
						],
					},
				},
			},
			...workerRoutes,
			...clientsRoutes,
			...activeProposalRoutesInApp,
			...paymentRequestRoutes,
			...agencyRoutes,
			...userRoutes,
			...companyNeedRoutes,
			...designReviewRoutes,
		],
	},
	...authRoutes,
];

const router = createRouter({
	history: createWebHistory('/'),
	routes,
});

router.afterEach(async (to) => {
	if (
		import.meta.env.VITE_MATOMO_FOLLOWING === 'true' &&
		// biome-ignore lint/suspicious/noExplicitAny: Init Biome
		(window as any)._paq
	) {
		// biome-ignore lint/suspicious/noExplicitAny: Init Biome
		const _paq = (window as any)._paq;

		if (Array.isArray(_paq)) {
			_paq.push(['setCustomUrl', window.location.href]);
			_paq.push(['setDocumentTitle', document.title]);
			_paq.push(['trackPageView']);
			_paq.push(['setExcludedQueryParams', ['logas?token']]);
			_paq.push(['enableLinkTracking']);
		} else if (_paq && typeof _paq.push === 'function') {
			_paq.push(['setCustomUrl', window.location.href]);
			_paq.push(['setDocumentTitle', document.title]);
			_paq.push(['trackPageView']);
			_paq.push(['setExcludedQueryParams', ['logas?token']]);
			_paq.push(['enableLinkTracking']);
		} else {
			console.error('❌ _paq format not recognized:', _paq);
		}
	} else if (!import.meta.env.VITE_MATOMO_FOLLOWING) {
		console.log(
			"🔧 Matomo tracking disabled (VITE_MATOMO_FOLLOWING not set to 'true')"
		);
		// biome-ignore lint/suspicious/noExplicitAny: Init Biome
	} else if (!(window as any)._paq) {
		console.error('❌ Matomo _paq not found!');
	} else {
		console.log('⚠️ Matomo condition not met');
	}

	try {
		// Do not execute the modal code if the route change is only a change of params (not path or name)
		if (
			to.name === router.currentRoute.value.name &&
			to.path === router.currentRoute.value.path
		) {
			console.log('⏭️ Same route, skipping modal cleanup');
			return;
		}
		console.log('to.name', to.name);
		console.log(
			'router.currentRoute.value.name',
			router.currentRoute.value.name
		);
		console.log('to.path', to.path);
		console.log(
			'router.currentRoute.value.path',
			router.currentRoute.value.path
		);
		const modalStore = useModalStore();
		modalStore.state.open.forEach(modalStore.actions.close);
	} catch (error) {
		console.warn('Modal store not available during route change:', error);
	}
});

router.beforeEach((to) => {
	const breadcrumbsStore = useBreadcrumbsStore();
	if (to.meta.breadcrumbs) {
		breadcrumbsStore.setBreadcrumbs(to.meta.breadcrumbs as BreadcrumbProps);
	}
});

router.onError((error, to) => {
	if (
		error.message.includes('Failed to fetch dynamically imported module') ||
		error.message.includes('Importing a module script failed')
	) {
		if (!to?.fullPath) {
			window.location.reload();
		} else {
			window.location.href = to.fullPath;
		}
	}
});

export default router;
